import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, MapPin, CheckCircle2 } from "lucide-react";
import { useLang } from "../context/LangContext";

export default function ProjectDetail({ project, onClose }) {
  const { lang, t } = useLang();
  const BackArrow = lang === "ar" ? ArrowRight : ArrowLeft;

  useEffect(() => {
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const goContact = () => {
    onClose();
    setTimeout(() => window.__lenis?.scrollTo("#contact", { offset: -72, duration: 1.4 }), 120);
  };

  return (
    <motion.div
      data-testid="project-detail-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[70] bg-[#FBF6E9] overflow-y-auto"
    >
      <div className="sticky top-0 z-10 bg-[#FBF6E9]/85 backdrop-blur-xl border-b border-[#E8DFC5]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <button
            data-testid="project-detail-back"
            onClick={onClose}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-stone-600 hover:text-emerald-600 transition-colors"
          >
            <BackArrow className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            {t.detail.back}
          </button>
          <span className="font-accent text-xs tracking-widest text-stone-500 hidden sm:block">
            {project.categoryLabel}
          </span>
        </div>
      </div>

      <div className="relative h-[46vh] lg:h-[58vh] overflow-hidden">
        <motion.img
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FBF6E9] via-[#FBF6E9]/45 to-transparent" />
        <div className="absolute bottom-0 inset-x-0">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 pb-8">
            <span className="inline-block rounded-full bg-emerald-500/15 border border-emerald-500/40 px-4 py-1.5 text-xs font-semibold text-emerald-700 mb-4">
              {project.categoryLabel}
            </span>
            <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-stone-900 leading-snug">
              {project.title}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12 lg:py-16">
        <div className="flex flex-wrap gap-3 mb-12">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#E8DFC5] bg-white px-4 py-2 text-sm text-stone-600">
            <MapPin className="w-4 h-4 text-emerald-500" />
            {project.location}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#E8DFC5] bg-white px-4 py-2 text-sm text-stone-600">
            {project.client}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mb-6">
              {t.detail.storyTitle}
            </h2>
            {project.story.map((p, i) => (
              <p key={i} className="text-base lg:text-lg leading-loose text-stone-500 mb-6">
                {p}
              </p>
            ))}
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-[#E8DFC5] bg-white p-7 lg:p-8">
              <h3 className="font-heading font-bold text-lg text-stone-900 mb-6">{t.detail.highlightsTitle}</h3>
              <ul className="flex flex-col gap-4">
                {project.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-base text-stone-600">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <h2 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mt-16 mb-8">
          {t.detail.galleryTitle}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {project.gallery.map((g, i) => (
            <figure
              key={i}
              data-testid={`project-gallery-${i}`}
              className={`group rounded-lg overflow-hidden clip-frame border border-[#E8DFC5] ${
                project.gallery.length % 2 === 1 && i === 0 ? "sm:col-span-2" : ""
              }`}
            >
              <img
                src={g.src}
                alt={g.caption}
                loading="lazy"
                className="w-full aspect-[16/10] object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <figcaption className="bg-white border-t border-[#E8DFC5] px-5 py-3 text-sm text-stone-500">
                {g.caption}
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-16 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-8 lg:p-10 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h3 className="font-heading font-bold text-xl text-stone-900 mb-2">
              {t.detail.ctaTitle}
            </h3>
            <p className="text-base text-stone-500">
              {t.detail.ctaText}
            </p>
          </div>
          <button
            data-testid="project-detail-cta"
            onClick={goContact}
            className="rounded-full bg-emerald-500 px-8 py-3.5 font-bold text-white transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_28px_rgba(16,185,129,0.4)]"
          >
            {t.detail.ctaBtn}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
